/**
 * R268ff · Feb 2026 — Row actions bar for GST Sales docs.
 *
 * Extracted from the monolithic `GstSales.jsx`. Handles:
 *   • View / Edit / Copy
 *   • Send / Approve / Convert (Estimate → SO → Invoice; Vendor Bill → DN)
 *   • Pay (invoices, vendor bills)
 *   • PDF download & WhatsApp share
 *
 * All API calls go through `@/lib/api` so the JWT header rides along.
 */
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Eye, Pencil, Copy, Wallet, Download, Send, Ban, Trash2,
  CheckCircle2, XCircle, GitBranch, Package, Receipt, ListChecks,
  ShieldCheck, Clock, MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import VoidReasonDialog from "./VoidReasonDialog";
import CreateSalesOrderDialog from "./CreateSalesOrderDialog";
import CreateInvoiceDialog from "./CreateInvoiceDialog";
import PushToTasksDialog from "./PushToTasksDialog";
import { useState } from "react";
import useWorkspacePerms from "@/lib/useWorkspacePerms";

export default function RowActions({ doc, onView, onPay, onEdit, onReload, onPatch, onRemove, ws, mobile }) {
  const btnCls = mobile ? "h-9 px-3 text-xs shrink-0" : "h-8 px-2 text-xs";
  const [showCreateSO, setShowCreateSO] = useState(false);
  const [showCreateInv, setShowCreateInv] = useState(false);
  const [showPushTasks, setShowPushTasks] = useState(false);
  const { can } = useWorkspacePerms();

  // Prefer in-place row refresh; fall back to a full `onReload` when
  // the parent hasn't wired `onPatch` yet (keeps old call-sites working).
  const refresh = () => {
    if (onPatch) onPatch(doc.id);
    else onReload?.();
  };
  const remove = () => {
    if (onRemove) onRemove(doc.id);
    else onReload?.();
  };

  // Feb 2026 R293zq · Approval workflow visibility.
  const APPROVE_PERM = { estimate: "quotation.approve",
                           sales_order: "sales_order.approve",
                           invoice: "invoice.approve" };
  const isApprovable = ["estimate", "sales_order", "invoice"].includes(doc.doc_type);
  const apStatus = doc.approval_status || (isApprovable ? "draft" : null);
  const canApprove = isApprovable && APPROVE_PERM[doc.doc_type]
    && can?.(APPROVE_PERM[doc.doc_type]);
  const canSubmit = isApprovable && (apStatus === "draft" || apStatus === "rejected");
  const canActApprove = canApprove && apStatus === "submitted";
  const canActReject = canApprove && apStatus === "submitted";
  // Only approved docs may be converted onwards.
  const canConvert = ["estimate", "sales_order", "invoice", "vendor_bill"].includes(doc.doc_type)
    && !["converted", "void", "cancelled", "paid", "superseded"].includes(doc.status)
    && (!isApprovable || apStatus === "approved");
  // R268r · Feb 2026 — Edit allowed on every doc except hard-locked terminal
  // statuses. Post-finalized edits require a reason.
  const canEdit = !["void", "cancelled", "rejected", "converted", "superseded"].includes(doc.status);
  // Feb 2026 — Void applies to anything already issued (sent, approved,
  // partial, overdue). Deletion is restricted to pristine drafts with
  // no money moved yet so we don't leak holes into the audit trail.
  const voidStates = { invoice: ["sent", "partially_paid", "overdue"],
                        vendor_bill: ["approved", "partially_paid", "overdue"],
                        credit_note: ["issued", "applied"],
                        debit_note: ["issued", "applied"],
                        estimate: ["sent", "accepted"],
                        sales_order: ["confirmed"],
                        payment_receipt: ["recorded"] };
  const canVoid = (voidStates[doc.doc_type] || []).includes(doc.status)
    && doc.status !== "void" && doc.status !== "cancelled";
  const canDelete = doc.status === "draft"
    && Number(doc.paid_total || 0) === 0
    && !doc.converted_to_id;

  const doCopy = async () => {
    try {
      const r = await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/duplicate`);
      toast.success(`Copied → ${r.data.doc_no}`);
      // A copy creates a NEW doc of the same type on the same tab,
      // so a full reload is the right call here.
      onReload?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const doConvert = async (to) => {
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/convert`, { to_type: to });
      toast.success(`Converted to ${to.replace("_", " ")}`);
      // Converting flips the parent's status (e.g. estimate.status→converted)
      // and may make it ineligible for the current tab's filter; patchRow
      // handles both cases (updates in place or drops the row).
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const doStatus = async (status) => {
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/status`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  // Feb 2026 R293u · Spawn a new estimate version from this one. Same
  // doc_no, bumped `version`, same root_estimate_id, resets to draft.
  const doNewVersion = async () => {
    try {
      const r = await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/new-version`);
      toast.success(`Started v${r.data.version} of ${doc.doc_no}`);
      onReload?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  // Feb 2026 R293zq · Approval workflow actions.
  const doSubmit = async () => {
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/submit`);
      toast.success("Submitted for approval");
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const doApprove = async () => {
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/approve`);
      toast.success("Approved");
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const doRejectApproval = async () => {
    const reason = window.prompt("Reason for rejection? (optional)") || "";
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/reject`, { reason });
      toast.success("Rejected · back to draft");
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/gst/workspaces/${ws}/documents/${doc.id}/pdf`,
                                  { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `${doc.doc_no || "document"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dlUrl), 1000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const shareWa = async () => {
    try {
      const r = await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/wa-share`, {});
      window.open(r.data.share_url, "_blank", "noopener");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const doVoid = () => setVoidOpen(true);
  const [voidOpen, setVoidOpen] = useState(false);

  const doDelete = async () => {
    const label = (doc.doc_type || "document").replace(/_/g, " ");
    if (!window.confirm(
      `Delete draft ${label} ${doc.doc_no || ""}?\n\nThis cannot be undone. The document number stays reserved so the sequence has a visible gap.`
    )) return;
    try {
      await api.delete(`/gst/workspaces/${ws}/documents/${doc.id}`);
      toast.success(`${doc.doc_no || "Draft"} deleted`);
      remove();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="inline-flex items-center gap-1 flex-nowrap justify-end">
      {/* Feb 2026 R293zq · Approval status chip. */}
      {isApprovable && apStatus && apStatus !== "approved" && (
        <span
          className={
            "inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 shrink-0 " +
            (apStatus === "submitted"
              ? "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800"
              : apStatus === "rejected"
                ? "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800"
                : "bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700")
          }
          data-testid={`gst-approval-chip-${doc.id}`}
          title={apStatus === "rejected" && doc.rejection_reason
            ? `Rejected · ${doc.rejection_reason}`
            : `Approval: ${apStatus}`}
        >
          <Clock className="h-2.5 w-2.5" />
          {apStatus}
        </span>
      )}
      {isApprovable && apStatus === "approved" && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800 shrink-0"
          data-testid={`gst-approval-chip-${doc.id}`}
          title={doc.approved_by_name
            ? `Approved by ${doc.approved_by_name}`
            : "Approved"}
        >
          <ShieldCheck className="h-2.5 w-2.5" />
          approved
        </span>
      )}

      {/* ===== PRIMARY CONTEXTUAL ACTIONS (inline, max 2-3) ===== */}
      {canSubmit && (
        <Button size="sm" variant="outline"
                  className={`${btnCls} border-amber-500 text-amber-800 hover:bg-amber-50 dark:text-amber-200 dark:hover:bg-amber-950/50 shrink-0`}
                  data-testid={`gst-action-submit-${doc.id}`}
                  onClick={doSubmit}
                  title="Submit this draft for approver review">
          <Send className="h-3.5 w-3.5 mr-1" /> Submit
        </Button>
      )}
      {canActApprove && (
        <Button size="sm" variant="outline"
                  className={`${btnCls} border-emerald-500 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/50 shrink-0`}
                  data-testid={`gst-action-approve-ap-${doc.id}`}
                  onClick={doApprove}
                  title="Approve this document — unlocks conversion & removes DRAFT watermark">
          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
      )}
      {canActReject && (
        <Button size="sm" variant="outline"
                  className={`${btnCls} border-rose-500 text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/50 shrink-0`}
                  data-testid={`gst-action-reject-ap-${doc.id}`}
                  onClick={doRejectApproval}
                  title="Reject — sends back to the accountant for edits">
          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      )}
      {/* Client-response actions on a sent estimate */}
      {doc.doc_type === "estimate" && doc.status === "sent" && (
        <>
          <Button size="sm" variant="outline"
                    className={`${btnCls} text-emerald-700 border-emerald-500 hover:bg-emerald-50 shrink-0`}
                    data-testid={`gst-action-accept-${doc.id}`}
                    onClick={() => doStatus("accepted")}
                    title="Client has approved this quotation version">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept
          </Button>
          <Button size="sm" variant="outline"
                    className={`${btnCls} text-rose-700 border-rose-500 hover:bg-rose-50 shrink-0`}
                    data-testid={`gst-action-reject-${doc.id}`}
                    onClick={() => doStatus("rejected")}
                    title="Client has rejected this version">
            <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
          </Button>
        </>
      )}
      {/* Create SO from accepted/superseded quote — gated on approval */}
      {doc.doc_type === "estimate" && (doc.status === "accepted" || doc.status === "superseded") && (
        apStatus === "approved" ? (
          <Button size="sm" variant="outline" className={`${btnCls} border-primary text-primary shrink-0`}
                    data-testid={`gst-action-create-so-${doc.id}`}
                    onClick={() => setShowCreateSO(true)}
                    title="Pick line items and spawn a Sales Order (partial quantities OK)">
            <Package className="h-3.5 w-3.5 mr-1" /> Create SO
          </Button>
        ) : (
          <span className="text-[10px] text-muted-foreground italic px-1 shrink-0 hidden lg:inline"
                data-testid={`gst-approve-first-${doc.id}`}
                title="Approve first to unlock Sales Order creation">
            Approve to create SO
          </span>
        )
      )}
      {/* SO → Invoice line picker — gated on approval */}
      {doc.doc_type === "sales_order" && !["cancelled", "void"].includes(doc.status) && (
        apStatus === "approved" ? (
          <Button size="sm" variant="outline" className={`${btnCls} border-primary text-primary shrink-0`}
                    data-testid={`gst-action-create-invoice-${doc.id}`}
                    onClick={() => setShowCreateInv(true)}
                    title="Pick line items and spawn an invoice.">
            <Receipt className="h-3.5 w-3.5 mr-1" /> Create invoice
          </Button>
        ) : (
          <span className="text-[10px] text-muted-foreground italic px-1 shrink-0 hidden lg:inline"
                data-testid={`gst-approve-first-${doc.id}`}
                title="Approve first to unlock invoicing">
            Approve to invoice
          </span>
        )
      )}
      {/* Pay — invoices + vendor bills */}
      {(doc.doc_type === "invoice" || doc.doc_type === "vendor_bill") && !["void", "paid"].includes(doc.status) && (
        <Button size="sm" variant="outline" className={`${btnCls} shrink-0`}
                  data-testid={`gst-action-pay-${doc.id}`}
                  onClick={onPay}>
          <Wallet className="h-3.5 w-3.5 mr-1" /> Pay
        </Button>
      )}
      {/* Vendor bill approve (non-approval-workflow legacy) */}
      {doc.doc_type === "vendor_bill" && doc.status === "draft" && (
        <Button size="sm" variant="outline" className={`${btnCls} shrink-0`}
                  data-testid={`gst-action-approve-${doc.id}`}
                  onClick={() => doStatus("approved")}>
          Approve
        </Button>
      )}

      {/* View — always visible, icon only to save space */}
      <Button size="sm" variant="ghost" className={`${btnCls} shrink-0 px-1.5`}
                data-testid={`gst-action-view-${doc.id}`}
                onClick={onView}
                title="Open details">
        <Eye className="h-3.5 w-3.5" />
      </Button>

      {/* ===== OVERFLOW MENU — everything else ===== */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className={`${btnCls} shrink-0 px-1.5`}
                    data-testid={`gst-action-more-${doc.id}`}
                    title="More actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid={`gst-action-menu-${doc.id}`}>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {doc.doc_no}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {canEdit && onEdit && (
            <DropdownMenuItem onSelect={onEdit} data-testid={`gst-action-edit-${doc.id}`}>
              <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={doCopy} data-testid={`gst-action-copy-${doc.id}`}>
            <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={downloadPdf} data-testid={`gst-action-pdf-${doc.id}`}>
            <Download className="h-3.5 w-3.5 mr-2" /> Download PDF
          </DropdownMenuItem>
          {doc.doc_type === "invoice" && (
            <DropdownMenuItem onSelect={shareWa} data-testid={`gst-action-wa-${doc.id}`}>
              <Send className="h-3.5 w-3.5 mr-2" /> Share on WhatsApp
            </DropdownMenuItem>
          )}
          {(doc.doc_type === "estimate" || doc.doc_type === "invoice") && doc.status === "draft" && (
            <DropdownMenuItem onSelect={() => doStatus("sent")} data-testid={`gst-action-send-${doc.id}`}>
              <Send className="h-3.5 w-3.5 mr-2" /> Mark as sent
            </DropdownMenuItem>
          )}
          {doc.doc_type === "estimate" && doc.status !== "draft" && (
            <DropdownMenuItem onSelect={doNewVersion} data-testid={`gst-action-new-version-${doc.id}`}>
              <GitBranch className="h-3.5 w-3.5 mr-2" /> New version
            </DropdownMenuItem>
          )}
          {canConvert && doc.doc_type === "estimate" && doc.status !== "accepted" && doc.status !== "superseded" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => doConvert("sales_order")} data-testid={`gst-action-convert-so-${doc.id}`}>
                <Package className="h-3.5 w-3.5 mr-2" /> Convert → Sales order
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => doConvert("invoice")} data-testid={`gst-action-convert-inv-${doc.id}`}>
                <Receipt className="h-3.5 w-3.5 mr-2" /> Convert → Invoice
              </DropdownMenuItem>
            </>
          )}
          {doc.doc_type === "sales_order" && !["cancelled", "void"].includes(doc.status) && (
            <DropdownMenuItem onSelect={() => setShowPushTasks(true)} data-testid={`gst-action-push-tasks-${doc.id}`}>
              <ListChecks className="h-3.5 w-3.5 mr-2" /> Push to tasks
            </DropdownMenuItem>
          )}
          {canConvert && doc.doc_type === "vendor_bill" && (
            <DropdownMenuItem onSelect={() => doConvert("debit_note")} data-testid={`gst-action-convert-dn-${doc.id}`}>
              <Receipt className="h-3.5 w-3.5 mr-2" /> Convert → Debit note
            </DropdownMenuItem>
          )}
          {doc.doc_type === "invoice" && ["sent", "partially_paid", "paid"].includes(doc.status) && (
            <DropdownMenuItem onSelect={() => doConvert("credit_note")} data-testid={`gst-action-cn-${doc.id}`}>
              <Receipt className="h-3.5 w-3.5 mr-2" /> Issue credit note
            </DropdownMenuItem>
          )}
          {(canVoid || canDelete) && <DropdownMenuSeparator />}
          {canVoid && (
            <DropdownMenuItem onSelect={doVoid} data-testid={`gst-action-void-${doc.id}`}
                              className="text-amber-700 focus:text-amber-800 focus:bg-amber-50">
              <Ban className="h-3.5 w-3.5 mr-2" /> Void
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem onSelect={doDelete} data-testid={`gst-action-delete-${doc.id}`}
                              className="text-rose-700 focus:text-rose-800 focus:bg-rose-50">
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete draft
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <VoidReasonDialog open={voidOpen} doc={doc} ws={ws}
                         onClose={() => setVoidOpen(false)}
                         onDone={() => { setVoidOpen(false); refresh(); }} />
      <CreateSalesOrderDialog
        open={showCreateSO}
        onClose={() => setShowCreateSO(false)}
        ws={ws}
        estimate={doc}
        onCreated={() => { setShowCreateSO(false); refresh(); }}
      />
      <CreateInvoiceDialog
        open={showCreateInv}
        onClose={() => setShowCreateInv(false)}
        ws={ws}
        so={doc}
        onCreated={() => { setShowCreateInv(false); refresh(); }}
      />
      <PushToTasksDialog
        open={showPushTasks}
        onClose={() => setShowPushTasks(false)}
        ws={ws}
        so={doc}
        onDone={() => { setShowPushTasks(false); refresh(); }}
      />
    </div>
  );
}
